// The form pickers beside the grids' cell editors (`editors.tsx`): a date with its calendar, and
// the carriers of A2000 and Deposco (item 8): picked from their list only, typing only searches it.
//
// The calendar of a form or a filter floats over the page, anchored to its field and flipped to
// stay on screen: it never moves or resizes what it is in. It opens on a click, Alt+↓ or (where
// Enter does not save the form) Enter — never on focus — and Escape closes only it.

import { useEffect, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, ReactNode, RefObject } from "react";
import { flushSync } from "react-dom";
import { Popover } from "@base-ui/react/popover";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  carrierLogText,
  carrierMatches,
  carrierOptionParts,
  carrierOptionText,
  carrierPick,
  optionOfCode,
} from "@app/shared";
import type { CarrierOption, CarrierPick } from "@app/shared";
import { Button, Calendar, Input } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { floatBox } from "./float-box";

const pad = (n: number) => String(n).padStart(2, "0");

/** A picked day as people type it: 10/26/26. */
export const typedDay = (d: Date) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${String(d.getFullYear()).slice(2)}`;

/** A typed date as the calendar's day, when it reads as one ("10/26", "10/26/26", "2026-10-26"). */
export function dayOfText(text: string, today: string): Date | undefined {
  const t = text.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12);
  const md = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/.exec(t);
  if (!md) return undefined;
  const year = md[3] ? (md[3].length === 2 ? 2000 + Number(md[3]) : Number(md[3])) : Number(today.slice(0, 4));
  const d = new Date(year, Number(md[1]) - 1, Number(md[2]), 12);
  return d.getMonth() === Number(md[1]) - 1 ? d : undefined;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const firstOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1, 12);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1, 12);

/** Today: an outline in the primary colour; the picked day is filled (the platform's). */
const TODAY = "rounded-(--cell-radius) font-semibold text-primary ring-1 ring-inset ring-primary/50";

/**
 * A calendar: the month's days (← → ↑ ↓ move the day, Enter picks it), its title a button to a
 * grid of months and years, Today and Clear. `range`: From–To shaded. Escape inside it is
 * `onEscape`'s (back to the field), never the dialog's.
 */
export function CalendarPanel({
  selected,
  range,
  today,
  onPick,
  onClear,
  onEscape,
  autoFocus,
  note,
}: {
  selected?: Date;
  range?: { from?: Date; to?: Date };
  today: string;
  onPick: (d: Date) => void;
  onClear?: () => void;
  onEscape?: () => void;
  /** Opened from the keyboard: the focus on the picked day (or today). */
  autoFocus?: boolean;
  note?: ReactNode;
}) {
  const todayDate = dayOfText(today, today) ?? new Date();
  const shown = selected ?? range?.to ?? range?.from;
  const [month, setMonth] = useState(() => firstOf(shown ?? todayDate));
  const [view, setView] = useState<"days" | "months">("days");
  // A typed date in another month moves the calendar to it.
  const shownMonth = shown ? `${shown.getFullYear()}-${shown.getMonth()}` : "";
  useEffect(() => {
    if (shown) setMonth(firstOf(shown));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the month only, not each new Date
  }, [shownMonth]);
  // From the keyboard: the focus on the picked day, else today, else the 1st — the arrows move it.
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!autoFocus) return;
    const id = requestAnimationFrame(() => {
      const days = box.current;
      const day =
        days?.querySelector<HTMLButtonElement>("td[data-selected=true] button") ??
        days?.querySelector<HTMLButtonElement>("td[data-today=true] button") ??
        days?.querySelector<HTMLButtonElement>("td:not([data-outside=true]) button");
      day?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);
  const y = month.getFullYear();
  const m = month.getMonth();
  const step = (n: number) => setMonth(addMonths(month, view === "days" ? n : n * 12));
  const days = {
    onDayClick: (d: Date) => onPick(d),
    month,
    onMonthChange: setMonth,
    hideNavigation: true,
    classNames: { month_caption: "hidden", today: TODAY },
    className: "p-0",
  };
  return (
    <div
      ref={box}
      className="w-[17rem] p-2 text-[13px]"
      // Caught on the way down: react-day-picker stops its arrow keys from bubbling.
      onKeyDownCapture={(e) => {
        // OUTSIDE-PLATFORM: P15 the platform's day button never takes the focus react-day-picker
        // moves (its ref is not on the button): the day it marked is focused here.
        if (/^(Arrow|Home$|End$|Page)/.test(e.key))
          requestAnimationFrame(() =>
            box.current?.querySelector<HTMLButtonElement>("td[data-focused=true] button")?.focus(),
          );
      }}
      onKeyDown={(e) => {
        // Its keys are its own: in a grid cell's editor (a portal), they would reach the grid.
        e.stopPropagation();
        if (e.key !== "Escape") return;
        e.preventDefault();
        if (view === "months") setView("days");
        else onEscape?.();
      }}
    >
      <div className="flex items-center justify-between gap-1 pb-1">
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={view === "days" ? "Previous month" : "Previous year"}
          onClick={() => step(-1)}
        >
          <ChevronLeftIcon />
        </Button>
        <button
          type="button"
          className="hover:bg-muted rounded px-2 py-1 font-medium"
          aria-label={view === "days" ? "Pick a month or a year" : "Back to the days"}
          onClick={() => setView(view === "days" ? "months" : "days")}
        >
          {view === "days" ? `${MONTH_NAMES[m]} ${y}` : y}
        </button>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={view === "days" ? "Next month" : "Next year"}
          onClick={() => step(1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      {view === "months" ? (
        <div className="grid grid-cols-3 gap-1 py-1" aria-label={`Months of ${y}`}>
          {MONTHS.map((name, i) => (
            <button
              key={name}
              type="button"
              autoFocus={i === m}
              className={cn(
                "hover:bg-muted rounded py-2",
                i === m && "bg-primary text-primary-foreground hover:bg-primary",
                y === todayDate.getFullYear() && i === todayDate.getMonth() && i !== m && TODAY,
              )}
              onClick={() => {
                setMonth(new Date(y, i, 1, 12));
                setView("days");
              }}
            >
              {name}
            </button>
          ))}
        </div>
      ) : range ? (
        <Calendar mode="range" selected={range.from ? { from: range.from, to: range.to } : undefined} {...days} />
      ) : (
        <Calendar mode="single" selected={selected} {...days} />
      )}
      {note}
      <div className="mt-1 flex justify-between border-t pt-1.5">
        <Button size="xs" variant="ghost" onClick={() => onPick(todayDate)}>
          Today
        </Button>
        {onClear ? (
          <Button size="xs" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A panel floating over the page at its field (OUTSIDE-PLATFORM: P7 the platform's PopoverContent
 * takes no `anchor`, so this is Base UI's Popover with one). It flips above when there is no room
 * under the field, never takes the focus by opening, and a click in it leaves the focus where it
 * is. A press on its own field is not "outside".
 */
export function AnchoredPopover({
  anchor,
  open,
  onClose,
  popupRef,
  children,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  popupRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  return (
    <Popover.Root
      open={open}
      modal={false}
      onOpenChange={(next, details) => {
        if (next) return;
        const t = details.event?.target;
        if (details.reason === "outside-press" && t instanceof Node && anchor.current?.contains(t)) return;
        onClose();
      }}
    >
      <Popover.Portal>
        <Popover.Positioner anchor={anchor} side="bottom" align="start" sideOffset={4} className="isolate z-50">
          <Popover.Popup
            ref={popupRef}
            initialFocus={false}
            finalFocus={false}
            className="bg-popover text-popover-foreground ring-foreground/10 rounded-md shadow-lg ring-1 outline-none"
            // A click on a day or a button leaves the focus where it is (the field, or the day).
            onMouseDown={(e) => e.preventDefault()}
          >
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * What opens a field's calendar from the keyboard: Alt+↓, and Enter where Enter does not save
 * the form.
 */
const opensCalendar = (e: KeyboardEvent, enterOpens: boolean) =>
  (e.key === "ArrowDown" && e.altKey) || (enterOpens && e.key === "Enter");

/**
 * A date field of a form: typed as before ("10/26"), the calendar floating under it on a click,
 * Alt+↓ or Enter (`enterOpens`: off in a dialog, where Enter saves). Picking a day fills the
 * field and closes it; Escape closes only it; Tab closes it and moves on.
 */
export function DateField({
  value,
  onChange,
  today,
  inputRef,
  enterOpens = true,
  className,
  ...input
}: {
  value: string;
  onChange: (v: string) => void;
  today: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  enterOpens?: boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
}) {
  const own = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? own;
  const popup = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<false | "mouse" | "keys">(false);
  const close = (refocus = false) => {
    setOpen(false);
    if (refocus) ref.current?.focus();
  };
  return (
    <div className={className}>
      <Input
        ref={ref}
        {...input}
        aria-haspopup="dialog"
        aria-expanded={!!open}
        placeholder={input.placeholder ?? "MM/DD"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={() => setOpen((o) => o || "mouse")}
        onBlur={(e) => {
          if (!(e.relatedTarget instanceof Node && popup.current?.contains(e.relatedTarget))) close();
        }}
        onKeyDown={(e) => {
          if (!open && opensCalendar(e, enterOpens)) {
            e.preventDefault();
            setOpen("keys");
          } else if (open && e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            close();
          } else if (open && e.key === "Tab") {
            // Closed before the focus moves, so it moves to the next field, not into the calendar.
            flushSync(() => setOpen(false));
          }
        }}
      />
      <AnchoredPopover anchor={ref} open={!!open} onClose={() => close()} popupRef={popup}>
        <CalendarPanel
          selected={dayOfText(value, today)}
          today={today}
          autoFocus={open === "keys"}
          onPick={(d) => {
            onChange(typedDay(d));
            close(true);
          }}
          onClear={() => {
            onChange("");
            close(true);
          }}
          onEscape={() => close(true)}
        />
      </AnchoredPopover>
    </div>
  );
}

/**
 * A date filter's From and To: typed ("10/01"), or picked on one calendar that follows the field
 * being filled, with the range between shaded. Picking From moves on to To; a To before From is
 * refused.
 */
export function DayInputs({
  from,
  to,
  today,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  today: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [field, setField] = useState<"from" | "to">("from");
  const [open, setOpen] = useState<false | "mouse" | "keys">(false);
  const [refused, setRefused] = useState("");
  const a = dayOfText(from, today);
  const b = dayOfText(to, today);
  const close = (refocus = false) => {
    setOpen(false);
    setRefused("");
    if (refocus) (field === "from" ? fromRef : toRef).current?.focus();
  };
  const pick = (d: Date) => {
    if (field === "from") {
      onFrom(typedDay(d));
      // A To already before the new From goes: the range is picked again from here.
      if (b && b < d) onTo("");
      setRefused("");
      setField("to");
      toRef.current?.focus();
      return;
    }
    if (a && d < a) return setRefused(`To can't be before From (${typedDay(a)})`);
    onTo(typedDay(d));
    close(true);
  };
  const input = (
    which: "from" | "to",
    ref: RefObject<HTMLInputElement | null>,
    value: string,
    set: (v: string) => void,
    label: string,
  ) => (
    <Input
      ref={ref}
      className="h-8"
      placeholder={label}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={!!open && field === which}
      value={value}
      onChange={(e) => set(e.target.value)}
      onFocus={() => {
        setField(which);
        setRefused("");
      }}
      onClick={() => setOpen((o) => o || "mouse")}
      onBlur={(e) => {
        const next = e.relatedTarget;
        if (
          next instanceof Node &&
          (popup.current?.contains(next) || next === fromRef.current || next === toRef.current)
        )
          return;
        close();
      }}
      onKeyDown={(e) => {
        if (!open && opensCalendar(e, true)) {
          e.preventDefault();
          setOpen("keys");
        } else if (open && e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
    />
  );
  return (
    <div className="flex items-center gap-2">
      {input("from", fromRef, from, onFrom, "From MM/DD")}
      {input("to", toRef, to, onTo, "To MM/DD")}
      <AnchoredPopover
        anchor={field === "from" ? fromRef : toRef}
        open={!!open}
        onClose={() => close()}
        popupRef={popup}
      >
        <CalendarPanel
          range={{ from: a, to: b }}
          today={today}
          autoFocus={open === "keys"}
          onPick={pick}
          onClear={() => {
            (field === "from" ? onFrom : onTo)("");
            setRefused("");
          }}
          onEscape={() => close(true)}
          note={
            <p className={cn("px-1 pt-1 text-xs", refused ? "text-destructive" : "text-muted-foreground")}>
              {refused || (field === "from" ? "Pick the first day" : "Pick the last day")}
            </p>
          }
        />
      </AnchoredPopover>
    </div>
  );
}

/**
 * The carriers every carrier field offers (item 8): A2000's and Deposco's list only
 * (`carrierOptions`), never a typed one. Unavailable: the field says so, with a retry, and the form
 * goes on with no carrier.
 */
export interface CarrierSourceApi {
  options: readonly CarrierOption[];
  status: "ready" | "loading" | "failed";
  retry: () => void;
}

/**
 * The carrier list as a picker shows it: opened, every carrier with the current one highlighted;
 * typing filters by name, code or SCAC and never offers a new value. ↑ ↓ move the highlight; Enter or
 * Tab take it only once the list was used (typed in or moved through) — else Enter is the form's.
 */
export function useCarrierList(query: string, options: readonly CarrierOption[], current: string, startTyped = false) {
  const [typed, setTyped] = useState(startTyped);
  const [moved, setMoved] = useState(false);
  const items = typed ? options.filter((o) => carrierMatches(o, query)) : [...options];
  const at = items.findIndex((o) => o.key === current);
  const [active, setActive] = useState(-1);
  const shown = active >= 0 && active < items.length ? active : typed ? 0 : at;
  return {
    items,
    active: shown,
    setActive,
    /** Something typed: the list narrows to it. */
    typedIn: () => {
      setTyped(true);
      setActive(-1);
    },
    reset: () => {
      setTyped(false);
      setMoved(false);
      setActive(-1);
    },
    /** ↑ ↓; true when the key was the list's. */
    arrows: (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return false;
      e.preventDefault();
      setMoved(true);
      setActive(Math.max(0, Math.min(items.length - 1, shown + (e.key === "ArrowDown" ? 1 : -1))));
      return true;
    },
    /** What Enter or Tab takes: the highlighted carrier once the list was used, else nothing. */
    picked: () => ((typed || moved) && shown >= 0 && shown < items.length ? items[shown]! : null),
  };
}

export type CarrierList = ReturnType<typeof useCarrierList>;

/**
 * Every carrier list's width (the Shipping cell's, a form's): "NAME · CODE · SCAC" and the tag whole
 * for all 447 carriers on staging (11/2026), the widest "ALWAYS FORWARDING INTERNATIONAL INC · ALLWAYS ·
 * AWFD" at ~475 px. A field wider keeps its own; a phone gets the window less 16 px (`floatBox`).
 */
export const CARRIER_LIST_WIDTH = 480;

/**
 * A carrier option in a list: "PERFORMANCE TEAM · PERF · PRFM", its source as a small tag. Too long,
 * only the name is cut — the code and SCAC stay (`carrierOptionParts`); the row's hover says it all.
 */
function CarrierOptionText({ option }: { option: CarrierOption }) {
  const { name, rest } = carrierOptionParts(option);
  return (
    <>
      <span className="flex min-w-0 gap-1">
        <span className="min-w-0 truncate">{name}</span>
        {rest ? <span className="shrink-0">{rest}</span> : null}
      </span>
      <span
        className={cn(
          "shrink-0 rounded px-1 text-[10px] font-semibold",
          option.source === "Deposco" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800",
        )}
      >
        {option.source}
      </span>
    </>
  );
}

/** The list under a carrier field: the source's carriers only, and No carrier. */
export function CarrierOptions({
  list,
  onPick,
  onClear,
}: {
  list: CarrierList;
  onPick: (option: CarrierOption) => void;
  /** "No carrier": the carrier is optional. */
  onClear?: () => void;
}) {
  return (
    <div onMouseDown={(e) => e.preventDefault()}>
      <ul role="listbox" className="max-h-60 overflow-y-auto py-1 text-[13px]" data-testid="carrier-options">
        {list.items.map((o, i) => (
          <li
            key={o.key}
            role="option"
            aria-selected={i === list.active}
            ref={(el) => {
              if (el && i === list.active) el.scrollIntoView({ block: "nearest" });
            }}
            className={cn(
              "flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5",
              i === list.active && "bg-accent text-accent-foreground",
            )}
            title={carrierOptionText(o)}
            onMouseEnter={() => list.setActive(i)}
            onClick={() => onPick(o)}
          >
            <CarrierOptionText option={o} />
          </li>
        ))}
        {!list.items.length ? <li className="text-muted-foreground px-3 py-1.5">No carrier matches</li> : null}
      </ul>
      <div className="text-muted-foreground flex items-center justify-between border-t px-3 py-1.5 text-xs">
        <span>From A2000 and Deposco · type to search</span>
        {onClear ? (
          <button type="button" className="text-primary hover:underline" onClick={onClear}>
            No carrier
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** "Carrier list unavailable — try again": the field when the carriers could not be read. */
export function CarriersUnavailable({ carriers }: { carriers: CarrierSourceApi }) {
  return (
    <p className="flex h-9 items-center gap-1.5 text-xs text-red-700" data-testid="carriers-unavailable">
      Carrier list unavailable —
      <button type="button" className="font-medium underline" onClick={carriers.retry}>
        try again
      </button>
    </p>
  );
}

/**
 * A carrier field of a form: the source's carriers under it while it has the focus; what is typed
 * only searches them. Leaving it shows the picked carrier again (or none). A carrier typed before
 * (a pickup saved before item 8) is said under it ("was: …") until one is picked.
 */
export function CarrierField({
  value,
  onChange,
  carriers,
  disabled,
  placeholder,
  was,
}: {
  value: CarrierPick | null;
  onChange: (v: CarrierPick | null) => void;
  carriers: CarrierSourceApi;
  disabled?: boolean;
  placeholder?: string;
  /** The old free-text carrier, shown until one is picked. */
  was?: string;
}) {
  const [open, setOpen] = useState(false);
  // Where the list goes, from the field's place when it gets the focus: under it, inside the window.
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  const [query, setQuery] = useState("");
  const current = value ? (optionOfCode(carriers.options, value.carrierSource, value.carrierCode)?.key ?? "") : "";
  const list = useCarrierList(query, carriers.options, current);
  const close = () => {
    setOpen(false);
    setQuery("");
    list.reset();
  };
  const pick = (o: CarrierOption | null) => {
    close();
    onChange(o ? carrierPick(o) : null);
  };
  if (carriers.status === "failed") return <CarriersUnavailable carriers={carriers} />;
  const shownValue = open ? query : value ? carrierLogText(value) : "";
  return (
    <div className="relative">
      <Input
        role="combobox"
        aria-expanded={open}
        aria-label="Carrier"
        value={shownValue}
        placeholder={carriers.status === "loading" ? "Loading carriers…" : (placeholder ?? "Pick a carrier")}
        disabled={disabled || carriers.status === "loading"}
        onChange={(e) => {
          setQuery(e.target.value);
          list.typedIn();
          setOpen(true);
        }}
        onFocus={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const at = floatBox(rect, Math.max(rect.width, CARRIER_LIST_WIDTH), window.innerWidth);
          setBox({ left: at.left - rect.left, width: at.width });
          setOpen(true);
        }}
        onBlur={close}
        onKeyDown={(e) => {
          if (!open) return;
          if (list.arrows(e)) return;
          if (e.key === "Enter" || e.key === "Tab") {
            const p = list.picked();
            if (p === null) return;
            // Enter takes the carrier, not the form's main button; Tab takes it and moves on.
            if (e.key === "Enter") e.preventDefault();
            pick(p);
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
        }}
      />
      {was && !value ? <p className="text-muted-foreground mt-0.5 text-[11px]">was: {was}</p> : null}
      {open ? (
        <div
          className="bg-popover text-popover-foreground ring-foreground/10 absolute top-full left-0 z-50 mt-1 w-full rounded-md shadow-lg ring-1"
          style={box ? { left: box.left, width: box.width } : { minWidth: CARRIER_LIST_WIDTH }}
        >
          <CarrierOptions list={list} onPick={pick} onClear={value ? () => pick(null) : undefined} />
        </div>
      ) : null}
    </div>
  );
}
